import axios from 'axios';

// Loud warning if the admin frontend is deployed WITHOUT a proper
// VITE_API_URL. Otherwise axios falls back to '/' — same host as the
// admin subdomain, which just serves the SPA index.html for every
// path including /api/*. That was the 2026-07-07 root cause of the
// "admin enters directly but shows no data" bug. Dev/local is fine
// because Vite proxies /api → Laravel; production without an env is
// broken by definition.
const configuredBaseUrl = import.meta.env.VITE_API_URL as string | undefined;
if (import.meta.env.PROD && !configuredBaseUrl) {
    // eslint-disable-next-line no-console
    console.warn(
        '[admin] VITE_API_URL is not set. The admin panel will send API requests to itself; ' +
        'set VITE_API_URL to the Laravel API host in .env.production before building.'
    );
}

const http = axios.create({
    baseURL: configuredBaseUrl || '/',
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

export async function csrf() {
    await http.get('/sanctum/csrf-cookie');
}

http.interceptors.response.use(
    (r) => {
        if (r.status === 204 || r.status === 205) return r;
        // Guard against SPA-fallback HTML being served as a 200 for an
        // /api/* OR /sanctum/* endpoint (common misconfig when the admin
        // panel is deployed to a subdomain without a Laravel API proxy).
        // Was 2026-07-07 root cause of "enters directly but shows no
        // data": HTML string was truthy → router assumed authenticated.
        // Widened 2026-07-09 to cover /sanctum/csrf-cookie too — the
        // csrf() call was silently accepting HTML which set no XSRF
        // cookie and guaranteed the next POST would fail.
        const ct = (r.headers?.['content-type'] || '').toString().toLowerCase();
        const url = (r.config?.url || '').toString();
        const isApiOrSanctum = url.startsWith('/api/') || url.startsWith('/sanctum/');
        if (isApiOrSanctum && !ct.includes('application/json') && typeof r.data === 'string') {
            // Wrap the HTML body in an object so downstream `data?.message`
            // lookups yield a real, translatable diagnostic instead of
            // undefined. Login.vue branches on status 502 for exactly
            // this synthetic response.
            const err: any = new Error(
                'Non-JSON response from an /api/ or /sanctum/ endpoint — CORS or proxy misconfig'
            );
            err.response = {
                status: 502,
                data: {
                    message: 'server_misconfigured',
                    hint: 'The admin SPA received HTML from an API endpoint. Check VITE_API_URL, CORS, or the Apache/Nginx vhost.',
                    html_preview: r.data.slice(0, 200),
                },
            };
            return Promise.reject(err);
        }
        return r;
    },
    (error) => {
        // Skip the hard redirect if the user is already on /login —
        // otherwise a 401 from POST /api/admin/login wipes the error
        // banner via full reload and looks like "nothing happened".
        const onLogin = window.location.pathname.startsWith('/login');
        if (error.response?.status === 401 && !onLogin) {
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default http;
